import { photos } from "@/features/portal/content/photos";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/**
 * What the drawing "recognises" on the plate. Sample numbers, nobody's real meal. The spots stay in
 * the upper two thirds, clear of the shutter button, even on the short cards.
 */
const FOUND = [
  { id: "chicken", name: () => m.portal_scan_chicken(), kcal: 297, spot: "left-[8%] top-[12%]" },
  { id: "rice", name: () => m.portal_scan_rice(), kcal: 195, spot: "right-[8%] top-[34%] [animation-delay:0.2s]" },
  {
    id: "broccoli",
    name: () => m.portal_scan_broccoli(),
    kcal: 41,
    spot: "left-[12%] top-[56%] [animation-delay:0.4s]",
  },
];

/** Corner brackets of a camera frame, drawn with eight gradients so nothing covers the photo. */
const corners =
  "[background:linear-gradient(var(--primary),var(--primary))_top_left/26px_3px_no-repeat,linear-gradient(var(--primary),var(--primary))_top_left/3px_26px_no-repeat,linear-gradient(var(--primary),var(--primary))_top_right/26px_3px_no-repeat,linear-gradient(var(--primary),var(--primary))_top_right/3px_26px_no-repeat,linear-gradient(var(--primary),var(--primary))_bottom_left/26px_3px_no-repeat,linear-gradient(var(--primary),var(--primary))_bottom_left/3px_26px_no-repeat,linear-gradient(var(--primary),var(--primary))_bottom_right/26px_3px_no-repeat,linear-gradient(var(--primary),var(--primary))_bottom_right/3px_26px_no-repeat]";

const keyframes = `
@keyframes portal-scan { 0% { top: 14px; opacity: 0 } 8% { opacity: 1 } 55% { top: calc(100% - 16px); opacity: 1 } 62%, 100% { top: calc(100% - 16px); opacity: 0 } }
@keyframes portal-found { 0%, 30% { opacity: 0; transform: scale(.9) translateY(6px) } 40%, 92% { opacity: 1; transform: none } 100% { opacity: 0 } }
`;

/**
 * A plate seen through the camera: a line sweeps down and the foods it finds pop up with their
 * calories. It illustrates counting calories from a photo; nothing is analysed.
 */
export const MealScan = ({ className }: { className?: string }) => (
  <div aria-hidden className={cn("relative isolate overflow-hidden rounded-[22px]", className)}>
    <style>{keyframes}</style>
    <img src={photos.mealPrep} alt="" loading="lazy" className="absolute inset-0 -z-10 size-full object-cover" />
    <span className="absolute inset-0 -z-10 bg-linear-to-t from-black/55 to-transparent to-45%" />
    {/* No radius on the frame: a rounded box clips its own background, which is what draws the corners. */}
    <span className={cn("absolute inset-3.5", corners)} />
    <span className="absolute inset-x-3.5 h-0.5 animate-[portal-scan_3.2s_cubic-bezier(0.2,0.8,0.2,1)_infinite] bg-primary shadow-[0_0_18px_4px_color-mix(in_oklch,var(--primary)_60%,transparent)] motion-reduce:hidden" />
    {FOUND.map((food) => (
      <span
        key={food.id}
        className={cn(
          "absolute inline-flex h-[30px] animate-[portal-found_3.2s_cubic-bezier(0.2,0.8,0.2,1)_infinite] items-center gap-1.5 rounded-full border border-white/10 bg-black/60 pr-2.5 pl-2 text-xs font-semibold whitespace-nowrap text-white backdrop-blur-xl motion-reduce:animate-none",
          food.spot,
        )}
      >
        <i className="size-2 rounded-full bg-primary" />
        {food.name()}
        <em className="text-primary not-italic">{m.portal_kcal({ amount: food.kcal })}</em>
      </span>
    ))}
    <span className="absolute bottom-4 left-1/2 grid size-[54px] -translate-x-1/2 place-items-center rounded-full border-[3px] border-white">
      <span className="size-10 rounded-full bg-white" />
    </span>
  </div>
);
